import { $, $$, sendForm, setBusy, toast } from './lib.js';

/**
 * Lesson editor: inline edit and partial regeneration of blocks, sections
 * and questions. The server returns the re-rendered fragment; we swap it in.
 * Every regeneration can be undone by re-saving the previous values.
 */
export function initEditor() {
  const editor = $('[data-lesson-editor]');
  if (!editor) return;

  editor.addEventListener('click', (event) => {
    const toggle = event.target.closest('[data-edit-toggle]');
    if (toggle) {
      const block = toggle.closest('[data-block]');
      const form = $('[data-edit-form]', block);
      form.hidden = !form.hidden;
      toggle.setAttribute('aria-expanded', String(!form.hidden));
      if (!form.hidden) form.querySelector('input:not([type=hidden]), textarea, select')?.focus();
    }
    const cancel = event.target.closest('[data-edit-cancel]');
    if (cancel) {
      const form = cancel.closest('[data-edit-form]');
      form.reset();
      form.hidden = true;
      const block = form.closest('[data-block]');
      $('[data-edit-toggle]', block)?.setAttribute('aria-expanded', 'false');
      $('[data-edit-toggle]', block)?.focus();
    }
  });

  editor.addEventListener('submit', async (event) => {
    const form = event.target.closest('form[data-async]');
    if (!form || event.defaultPrevented) return;
    event.preventDefault();

    const kind = form.dataset.async;
    const target = form.closest('[data-replaceable]');
    const block = form.closest('[data-block]') || target;
    const editForm = block ? $('[data-edit-form]', block) : null;
    const undoData = kind === 'regenerate' && editForm ? new FormData(editForm) : null;
    const button = form.querySelector('button[type="submit"]');

    document.querySelectorAll('[data-menu-panel]').forEach((p) => (p.hidden = true));
    target.classList.add('is-busy');
    target.setAttribute('aria-busy', 'true');
    if (kind !== 'edit') setBusy(button, true, kind === 'regenerate-section' ? 'Regenerating…' : '');
    const status = kind === 'regenerate' || kind === 'regenerate-section' ? showWorking(target) : null;

    const result = await sendForm(form);
    status?.remove();
    target.classList.remove('is-busy');
    target.removeAttribute('aria-busy');
    setBusy(button, false);

    if (!result.ok) {
      toast(result.data.error || 'That didn’t work. Nothing was changed.', { type: 'error' });
      return;
    }

    if (kind === 'delete') {
      toast(result.data.message || 'Removed.');
      window.location.reload();
      return;
    }

    const replacement = swap(target, result.data.html);
    toast(result.data.message || 'Saved.', {
      action: undoData
        ? {
            label: 'Undo',
            onClick: async () => {
              const form = $('[data-edit-form]', replacement);
              const undo = await sendForm(form, undoData);
              if (undo.ok) {
                swap(replacement, undo.data.html);
                toast('Restored the previous version.');
              } else {
                toast(undo.data.error || 'Couldn’t undo.', { type: 'error' });
              }
            },
          }
        : null,
    });
  });
}

function swap(target, html) {
  const template = document.createElement('template');
  template.innerHTML = html.trim();
  const fresh = template.content.firstElementChild;
  target.replaceWith(fresh);
  fresh.classList.add('is-flash');
  fresh.querySelector('[data-block-view], h2')?.setAttribute('tabindex', '-1');
  fresh.querySelector('[data-block-view], h2')?.focus({ preventScroll: true });
  return fresh;
}

function showWorking(target) {
  const note = document.createElement('p');
  note.className = 'flex items-center gap-2 px-3 pb-2 text-xs text-brand';
  note.setAttribute('role', 'status');
  note.innerHTML = '<span class="spinner h-3 w-3"></span>Writing a new version…';
  target.append(note);
  return note;
}
